import { useState } from 'react';
import './ArtistStatusPanel.css';
import type { ArtistStatus, TimerState } from '../../types';

const formatCountdown = (endTime: string | null, state: TimerState) => {
    if (state === 'not_started') return '—';
    if (!endTime || state === 'expired') return 'Expired';
    const ms = new Date(endTime).getTime() - Date.now();
    if (ms <= 0) return 'Expired';
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    if (h === 0 && m < 1) return '< 1m';
    if (h === 0) return `${m}m`;
    return `${h}h ${m}m`;
};

const notifLabel = (status: ArtistStatus['notificationStatus']) => {
    switch (status) {
        case 'sent':    return '✓ sent';
        case 'failed':  return '⚠ failed';
        case 'bounced': return '⚠ bounced';
        case 'pending': return 'pending';
        default:        return '—';
    }
};

function ArtistRow({ artist, onResend, resending }: {
    artist: ArtistStatus;
    onResend: (linkId: number) => void;
    resending: boolean;
}) {
    const { name, notificationStatus, timer, credits, sharedLinkId, canResend, accessCount } = artist;
    const countdown = formatCountdown(timer.endTime, timer.state);
    const isExpired = timer.state === 'expired' || (!!timer.endTime && new Date(timer.endTime) < new Date());

    return (
        <div className='asp-artist-row'>
            <span className='asp-artist-name'>{name}</span>
            <span className={`asp-notif asp-notif-${notificationStatus ?? 'pending'}`}>
                {notifLabel(notificationStatus)}
            </span>
            <span className={`asp-timer${isExpired ? ' asp-timer-expired' : ''}`}>
                {countdown}
            </span>
            <span className='asp-credit-pending'>{credits.pending} ⏳</span>
            <span className='asp-credit-approved'>{credits.approved + (credits.auto_approved ?? 0)} ✓</span>
            <span className='asp-credit-rejected'>{credits.rejected} ✗</span>
            <span className='asp-access-count'>
                {accessCount ?? 0} {accessCount === 1 ? 'open' : 'opens'}
            </span>
            <div className='asp-resend-col'>
                {canResend && sharedLinkId !== null && (
                    <button
                        className='asp-resend-btn'
                        onClick={() => onResend(sharedLinkId)}
                        disabled={resending}
                    >
                        {resending ? 'Sending…' : 'Resend'}
                    </button>
                )}
            </div>
        </div>
    );
}

export type ArtistStatusPanelProps = {
    artistStatus: ArtistStatus[];
    onResend: (linkId: number) => Promise<void>;
};

export default function ArtistStatusPanel({ artistStatus, onResend }: ArtistStatusPanelProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [resending, setResending] = useState<number | null>(null);
    const [resendError, setResendError] = useState('');

    const failedCount = artistStatus.filter(a =>
        (a.notificationStatus === 'failed' || a.notificationStatus === 'bounced') && a.canResend
    ).length;

    const handleResend = async (linkId: number) => {
        setResendError('');
        setResending(linkId);
        try {
            await onResend(linkId);
        } catch (err) {
            setResendError(`Resend failed: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
            setResending(null);
        }
    };

    return (
        <div className='asp-panel'>
            <button className='asp-toggle' onClick={() => setIsOpen(o => !o)}>
                <span className='asp-toggle-label'>Artist Status</span>
                {failedCount > 0 && (
                    <span className='asp-alert-badge'>{failedCount} need resend</span>
                )}
                <span className='asp-chevron'>{isOpen ? '▼' : '▲'}</span>
            </button>

            {isOpen && (
                <div className='asp-content'>
                    {resendError && (
                        <div className='asp-resend-error'>
                            {resendError}
                            <button className='asp-error-dismiss' onClick={() => setResendError('')}>×</button>
                        </div>
                    )}
                    {artistStatus.length === 0 && (
                        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 'var(--text-sm)', margin: 0 }}>
                            No artist data yet.
                        </p>
                    )}
                    {artistStatus.length > 0 && (
                        <div className='asp-table'>
                            <div className='asp-column-headers'>
                                <span>Artist</span>
                                <span>Email</span>
                                <span>Time Left</span>
                                <span>Pending</span>
                                <span>Approved</span>
                                <span>Rejected</span>
                                <span>Access Count</span>
                                <span></span>
                            </div>
                            {artistStatus.map(artist => (
                                <ArtistRow
                                    key={artist.userId}
                                    artist={artist}
                                    onResend={handleResend}
                                    resending={resending === artist.sharedLinkId}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}